/**
 * useForecastData
 *
 * Derives booking-based FORECAST utilisation metrics from the SchedulerContext.
 * Only forward-looking: bookings that start on or after today are considered.
 *
 * "Booked"   = confirmed bookings in the future window.
 * "Planned"  = planned (unconfirmed) bookings in the future window.
 * "Projected"= confirmed + planned combined.
 *
 * The hook intentionally mirrors the shape of useUtilisationData so that
 * UtilisationForecast.tsx can reuse all the same display components.
 */
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  eachWeekOfInterval,
  endOfWeek,
  startOfDay,
  max as dateMax,
  min as dateMin,
  parseISO,
  format,
  addDays,
} from 'date-fns';
import { useScreenData } from '@/hooks/useScreenData';
import { forecastBookingsApi } from '@/services/api';
import { mapApiBookingToBooking } from '@/hooks/use-date-range-bookings';
import { useCompany } from '@/contexts/CompanyContext';
import type { Booking, Resource, WorkingHoursProfile, AppSettings } from '@/types';

// ─── Public types ─────────────────────────────────────────────────────────────

export type ForecastViewBy = 'individual' | 'planning-group' | 'department' | 'location' | 'project-cohort';
export type ForecastType = 'booked' | 'planned' | 'both';
export type ForecastStatus =
  | 'on-target'
  | 'below-target'
  | 'overloaded'
  | 'no-bookings';

export interface ForecastRow {
  id: string;
  name: string;
  role: string;
  department: string;
  targetPct: number;
  /** Confirmed future bookings ÷ available hours */
  bookedPct: number;
  /** Confirmed + planned future bookings ÷ available hours */
  projectedPct: number;
  /** bookedPct − targetPct */
  variancePct: number;
  bookedHours: number;
  plannedHours: number;
  availableHours: number;
  status: ForecastStatus;
  initials: string;
  photoUrl?: string;
  bookedColour: string;
  periodBookings: ForecastBooking[];
  /** Projected % per upcoming week (next 12 weeks) */
  weeklyForecast: number[];
  /** Does this resource have an over-capacity week in the next 4 weeks? */
  hasRiskFlag: boolean;
  riskPct?: number;
  riskWeek?: string;
  memberCount: number;
}

export interface ForecastBooking {
  id: string;
  projectName: string;
  startDate: string;
  endDate: string;
  allocationPct: number;
  status: 'confirmed' | 'planned';
}

export interface ForecastChartPoint {
  name: string;
  booked: number;
  projected: number;
  target: number;
}

export interface ForecastSummary {
  avgBooked: number;
  avgProjected: number;
  totalBookedHours: number;
  resourcesOnTarget: number;
  totalResources: number;
  /** Resources whose projected utilisation exceeds 100% in any upcoming week */
  atRiskCount: number;
}

export interface ForecastData {
  rows: ForecastRow[];
  chartData: ForecastChartPoint[];
  summary: ForecastSummary;
  isEmpty: boolean;
  /** True while live-mode bookings are still being fetched for the first time */
  isLoading: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_TARGET_PCT = 75;

const DEFAULT_WORKING_PROFILE: WorkingHoursProfile = {
  monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8,
  saturday: 0, sunday: 0,
};

const DAY_KEYS: (keyof WorkingHoursProfile)[] = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

const TEAL  = 'var(--teal-500)';
const AMBER = 'var(--warning-500)';
const RED   = 'var(--destructive)';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = String(name ?? '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getEffectiveProfile(
  resource: Resource,
  globalProfile: WorkingHoursProfile,
): WorkingHoursProfile {
  if (resource.useCustomWorkingProfile && resource.customWorkingProfile) {
    return resource.customWorkingProfile;
  }
  return globalProfile;
}

function profileHoursForDay(dayOfWeek: number, profile: WorkingHoursProfile): number {
  return profile[DAY_KEYS[dayOfWeek]] ?? 0;
}

function workingHoursInInterval(start: Date, end: Date, profile: WorkingHoursProfile): number {
  let total = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    total += profileHoursForDay(cursor.getDay(), profile);
    cursor.setDate(cursor.getDate() + 1);
  }
  return total;
}

function durationFactor(durationType: Booking['durationType']): number {
  switch (durationType) {
    case 'half-day':
    case 'half-day-am':
    case 'half-day-pm':
      return 0.5;
    default:
      return 1;
  }
}

function bookingHoursInRange(
  booking: Booking,
  rangeStart: Date,
  rangeEnd: Date,
  profile: WorkingHoursProfile,
): number {
  const bStart = parseISO(booking.startTime);
  let bEnd     = parseISO(booking.endTime);

  // For day-based bookings where endTime <= startTime (e.g. single-day
  // bookings stored with the same timestamp for start and end), treat the
  // booking as spanning at least one full day by advancing the end to the
  // start of the next day.
  if (booking.durationType !== 'hourly' && bEnd.getTime() <= bStart.getTime()) {
    bEnd = addDays(startOfDay(bStart), 1);
  }

  const overlapStart = dateMax([bStart, rangeStart]);
  const overlapEnd   = dateMin([bEnd, rangeEnd]);
  if (overlapStart >= overlapEnd) return 0;

  // Hourly bookings: derive total hours from the start/end time span,
  // then pro-rate for partial overlap with the date range.
  if (booking.durationType === 'hourly') {
    const bookingMs = bEnd.getTime() - bStart.getTime();
    if (bookingMs > 0) {
      const totalHours = bookingMs / (1000 * 60 * 60);
      const overlapMs  = overlapEnd.getTime() - overlapStart.getTime();
      return totalHours * (overlapMs / bookingMs);
    }
    // Fall through to day-based calculation when start === end
  }

  // Day-based bookings: subtract 1 ms so the working-day count treats the
  // end as inclusive of the last booked day rather than spilling into the
  // following day.
  const inclusiveEnd = new Date(overlapEnd.getTime() - 1);
  if (inclusiveEnd < overlapStart) return 0;
  const hours = workingHoursInInterval(overlapStart, inclusiveEnd, profile);
  return hours * durationFactor(booking.durationType);
}

function resolveColour(pct: number, targetPct: number): string {
  if (pct > 100)              return RED;
  if (pct >= targetPct)       return TEAL;
  if (pct >= targetPct - 10)  return AMBER;
  return RED;
}

function resolveStatus(
  bookedPct: number,
  targetPct: number,
  bookedHours: number,
  plannedHours: number,
): ForecastStatus {
  if (bookedHours === 0 && plannedHours === 0) return 'no-bookings';
  if (bookedPct > 100)               return 'overloaded';
  if (bookedPct >= targetPct)        return 'on-target';
  return 'below-target';
}

function groupKeyFor(r: Resource, viewBy: ForecastViewBy): string {
  switch (viewBy) {
    case 'planning-group': return r.schedulerGroup ?? r.department ?? 'No planning group';
    case 'department':     return r.department ?? r.schedulerGroup ?? 'No department';
    case 'location':       return r.location ?? 'No location';
    default:               return r.id;
  }
}

// ─── Per-resource computation ─────────────────────────────────────────────────

interface ResourceForecastMetrics {
  resource: Resource;
  targetPct: number;
  bookedHours: number;
  plannedHours: number;
  availableHours: number;
  bookedPct: number;
  projectedPct: number;
  variancePct: number;
  periodBookings: ForecastBooking[];
  weeklyForecast: number[];
  hasRiskFlag: boolean;
  riskPct?: number;
  riskWeek?: string;
}

function computeResourceForecast(
  resource: Resource,
  resourceBookings: Booking[],
  dateFrom: Date,
  dateTo: Date,
  profile: WorkingHoursProfile,
  targetPct: number,
  next12WeekStarts: Date[],
  today: Date,
): ResourceForecastMetrics {
  const availableHoursTotal = workingHoursInInterval(dateFrom, dateTo, profile);
  // Non-cancelled bookings that overlap the selected forecast window.
  // The API already returns bookings filtered by the selected date range,
  // so we only need the overlap check here — no separate "today" guard.
  const periodBkgs = resourceBookings.filter((b) => {
    if (b.status === 'cancelled') return false;
    const bStart = parseISO(b.startTime);
    let bEnd     = parseISO(b.endTime);
    // Guard: same-timestamp bookings span at least one day
    if (b.durationType !== 'hourly' && bEnd.getTime() <= bStart.getTime()) {
      bEnd = addDays(startOfDay(bStart), 1);
    }
    return bStart < dateTo && bEnd > dateFrom;
  });

  let bookedHours  = 0;
  let plannedHours = 0;

  const periodBookings: ForecastBooking[] = periodBkgs.flatMap((b) => {
    const hrs = bookingHoursInRange(b, dateFrom, dateTo, profile);
    const isConfirmed = b.status === 'confirmed';
    if (isConfirmed) bookedHours  += hrs;
    else             plannedHours += hrs;

    if (hrs <= 0) return [];

    const bStartDate = parseISO(b.startTime);
    let bEndDate     = parseISO(b.endTime);
    // Same guard as bookingHoursInRange: treat same-timestamp bookings as 1 day
    if (b.durationType !== 'hourly' && bEndDate.getTime() <= bStartDate.getTime()) {
      bEndDate = addDays(startOfDay(bStartDate), 1);
    }
    const inclusiveBEnd = new Date(bEndDate.getTime() - 1);
    const totalBookingHours = workingHoursInInterval(bStartDate, inclusiveBEnd, profile) * durationFactor(b.durationType);
    const allocationPct = totalBookingHours > 0
      ? Math.round((hrs / totalBookingHours) * 100)
      : 0;
    if (allocationPct === 0) return [];

    return [{
      id:            b.id,
      projectName:   b.projectName,
      startDate:     format(parseISO(b.startTime), 'dd MMM yyyy'),
      endDate:       format(parseISO(b.endTime),   'dd MMM yyyy'),
      allocationPct,
      status:        isConfirmed ? 'confirmed' : 'planned',
    }];
  });

  const bookedPct = availableHoursTotal > 0
    ? Math.round((bookedHours / availableHoursTotal) * 100)
    : 0;
  const projectedPct = availableHoursTotal > 0
    ? Math.round(((bookedHours + plannedHours) / availableHoursTotal) * 100)
    : 0;
  const variancePct = bookedPct - targetPct;

  // Weekly forecast sparkline — projected % per upcoming week (next 12)
  const weeklyForecast = next12WeekStarts.map((ws) => {
    const we    = endOfWeek(ws, { weekStartsOn: 1 });
    const wAvail = workingHoursInInterval(ws, we, profile);
    if (wAvail === 0) return 0;
    let wHours = 0;
    for (const b of resourceBookings) {
      if (b.status !== 'cancelled') {
        wHours += bookingHoursInRange(b, ws, we, profile);
      }
    }
    return Math.round((wHours / wAvail) * 100);
  });

  // Risk flag — projected over-capacity in the next 4 weeks
  const near4 = weeklyForecast.slice(0, 4);
  const nearMax = Math.max(...near4);
  const hasRiskFlag = nearMax > 100;
  const riskWeekIdx = near4.findIndex((v) => v > 100);
  const riskWeek = hasRiskFlag && riskWeekIdx >= 0
    ? format(next12WeekStarts[riskWeekIdx], "'w/c' dd MMM")
    : undefined;

  return {
    resource,
    targetPct,
    bookedHours:    Math.round(bookedHours),
    plannedHours:   Math.round(plannedHours),
    availableHours: Math.round(Math.max(availableHoursTotal - bookedHours - plannedHours, 0)),
    bookedPct,
    projectedPct,
    variancePct,
    periodBookings,
    weeklyForecast,
    hasRiskFlag,
    riskPct:  hasRiskFlag ? nearMax   : undefined,
    riskWeek: hasRiskFlag ? riskWeek  : undefined,
  };
}

// ─── Aggregation ──────────────────────────────────────────────────────────────

function aggregateForecastGroup(
  groupKey: string,
  metrics: ResourceForecastMetrics[],
): ForecastRow {
  const totalBooked    = metrics.reduce((s, m) => s + m.bookedHours,    0);
  const totalPlanned   = metrics.reduce((s, m) => s + m.plannedHours,   0);
  const totalAvailable = metrics.reduce((s, m) => s + m.availableHours, 0);
  // Recover total capacity (available already has bookings subtracted)
  const totalCapacity  = totalAvailable + totalBooked + totalPlanned;

  const bookedPct = totalCapacity > 0
    ? Math.round((totalBooked / totalCapacity) * 100)
    : 0;
  const projectedPct = totalCapacity > 0
    ? Math.round(((totalBooked + totalPlanned) / totalCapacity) * 100)
    : 0;

  const avgTarget = metrics.length > 0
    ? Math.round(metrics.reduce((s, m) => s + m.targetPct, 0) / metrics.length)
    : DEFAULT_TARGET_PCT;

  const variancePct = bookedPct - avgTarget;

  const weeklyForecast = (metrics[0]?.weeklyForecast ?? []).map((_, wi) => {
    const vals = metrics.map((m) => m.weeklyForecast[wi] ?? 0);
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  });

  const hasRiskFlag = metrics.some((m) => m.hasRiskFlag);
  const maxRisk = hasRiskFlag
    ? Math.max(...metrics.filter((m) => m.hasRiskFlag).map((m) => m.riskPct ?? 0))
    : undefined;
  const riskWeek = hasRiskFlag
    ? metrics.find((m) => m.hasRiskFlag)?.riskWeek
    : undefined;

  const allBookings = metrics.flatMap((m) => m.periodBookings);
  const firstName   = metrics[0]?.resource.name ?? groupKey;
  const displayRole = metrics.length === 1 ? metrics[0].resource.role : `${metrics.length} members`;
  const dept        = metrics[0]?.resource.schedulerGroup
    ?? metrics[0]?.resource.department
    ?? '—';

  return {
    id:              groupKey,
    name:            groupKey,
    role:            displayRole,
    department:      dept,
    targetPct:       avgTarget,
    bookedPct,
    projectedPct,
    variancePct,
    bookedHours:     totalBooked,
    plannedHours:    totalPlanned,
    availableHours:  totalAvailable,
    status:          resolveStatus(bookedPct, avgTarget, totalBooked, totalPlanned),
    initials:        getInitials(firstName),
    bookedColour:    resolveColour(bookedPct, avgTarget),
    periodBookings:  allBookings,
    weeklyForecast,
    hasRiskFlag,
    riskPct:         maxRisk,
    riskWeek,
    memberCount:     metrics.length,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useForecastData(
  dateFrom: Date,
  dateTo: Date,
  viewBy: ForecastViewBy,
  selectedResourceIds: string[],
  selectedDepartments: string[],
  selectedTeams: string[],
  selectedRoles: string[],
): ForecastData {
  const { resources, bookings: contextBookings, settings, currentMode } = useScreenData('calendar');

  // ── Live-mode booking fetch ──────────────────────────────────────────────
  // Two separate pools: one for the user-selected filter range and one for
  // the 12-week sparkline window. This ensures the API call for the main
  // forecast metrics matches the selected filter dates exactly.
  const [liveFilterBookings, setLiveFilterBookings] = useState<Booking[]>([]);
  const [liveSparklineBookings, setLiveSparklineBookings] = useState<Booking[]>([]);
  const [hasFetchedFilter, setHasFetchedFilter] = useState(false);
  const [hasFetchedSparkline, setHasFetchedSparkline] = useState(false);
  const filterAbortRef = useRef<AbortController | null>(null);
  const sparklineAbortRef = useRef<AbortController | null>(null);
  const filterFetchIdRef = useRef(0);
  const sparklineFetchIdRef = useRef(0);

  const { currentCompany, isLoading: isCompanyLoading } = useCompany();
  const companyIdRef = useRef(currentCompany?.id);
  companyIdRef.current = currentCompany?.id;

  // Sparkline range: next 12 weeks from today (stable per render cycle)
  const sparklineRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return { start: today, end: addDays(today, 7 * 12) };
  }, []);

  // Fetch bookings for the user-selected filter range
  const fetchFilterBookings = useCallback(async (start: Date, end: Date) => {
    if (filterAbortRef.current) filterAbortRef.current.abort();
    filterAbortRef.current = new AbortController();
    const id = ++filterFetchIdRef.current;
    try {
      const apiBookings = await forecastBookingsApi.getByDateRange(
        start.toISOString(),
        end.toISOString(),
        false,
        companyIdRef.current,
      );
      if (filterAbortRef.current?.signal.aborted) return;
      setLiveFilterBookings(apiBookings.map(mapApiBookingToBooking));
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('Error fetching forecast filter bookings:', err);
      setLiveFilterBookings([]);
    } finally {
      if (filterFetchIdRef.current === id) setHasFetchedFilter(true);
    }
  }, []);

  // Fetch bookings for the 12-week sparkline range
  const fetchSparklineBookings = useCallback(async (start: Date, end: Date) => {
    if (sparklineAbortRef.current) sparklineAbortRef.current.abort();
    sparklineAbortRef.current = new AbortController();
    const id = ++sparklineFetchIdRef.current;
    try {
      const apiBookings = await forecastBookingsApi.getByDateRange(
        start.toISOString(),
        end.toISOString(),
        false,
        companyIdRef.current,
      );
      if (sparklineAbortRef.current?.signal.aborted) return;
      setLiveSparklineBookings(apiBookings.map(mapApiBookingToBooking));
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('Error fetching forecast sparkline bookings:', err);
      setLiveSparklineBookings([]);
    } finally {
      if (sparklineFetchIdRef.current === id) setHasFetchedSparkline(true);
    }
  }, []);

  // Reset fetch-tracking flags when leaving live mode so the next
  // live switch correctly shows a loading state.
  useEffect(() => {
    if (currentMode !== 'live') {
      setHasFetchedFilter(false);
      setHasFetchedSparkline(false);
    }
  }, [currentMode]);

  // Fire the filter-range fetch when mode/dates/company change
  useEffect(() => {
    if (currentMode !== 'live') return;
    if (isCompanyLoading || !currentCompany?.id) return;
    fetchFilterBookings(dateFrom, dateTo);
    return () => { filterAbortRef.current?.abort(); };
  }, [currentMode, dateFrom, dateTo, fetchFilterBookings, currentCompany?.id, isCompanyLoading]);

  // Fire the sparkline-range fetch when mode/company change (range is stable)
  useEffect(() => {
    if (currentMode !== 'live') return;
    if (isCompanyLoading || !currentCompany?.id) return;
    fetchSparklineBookings(sparklineRange.start, sparklineRange.end);
    return () => { sparklineAbortRef.current?.abort(); };
  }, [currentMode, sparklineRange.start, sparklineRange.end, fetchSparklineBookings, currentCompany?.id, isCompanyLoading]);

  // Merge filter + sparkline bookings (de-duplicate by id)
  const liveBookings = useMemo(() => {
    if (currentMode !== 'live') return [];
    const map = new Map<string, Booking>();
    for (const b of liveFilterBookings) map.set(b.id, b);
    for (const b of liveSparklineBookings) {
      if (!map.has(b.id)) map.set(b.id, b);
    }
    return Array.from(map.values());
  }, [currentMode, liveFilterBookings, liveSparklineBookings]);

  const bookings = currentMode === 'live' ? liveBookings : contextBookings;
  const isLiveLoading = currentMode === 'live' && (!hasFetchedFilter || !hasFetchedSparkline);

  const data = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const realResources = resources.filter(
      (r) => r.id !== 'unallocated' && r.status !== 'inactive',
    );

    const filtered = realResources.filter((r) => {
      if (selectedResourceIds.length > 0 && !selectedResourceIds.includes(r.id)) return false;
      if (selectedDepartments.length > 0 && !selectedDepartments.includes(r.department ?? '')) return false;
      if (selectedTeams.length > 0 && !selectedTeams.includes(r.schedulerGroup ?? '')) return false;
      if (selectedRoles.length > 0 && !selectedRoles.includes(r.role ?? '')) return false;
      return true;
    });

    if (filtered.length === 0) {
      return {
        rows: [],
        chartData: [],
        summary: {
          avgBooked: 0,
          avgProjected: 0,
          totalBookedHours: 0,
          resourcesOnTarget: 0,
          totalResources: 0,
          atRiskCount: 0,
        },
        isEmpty: true,
      };
    }

    const globalProfile: WorkingHoursProfile =
      (settings as unknown as AppSettings).globalWorkingProfile ?? DEFAULT_WORKING_PROFILE;

    const targetPct: number =
      (settings as unknown as Record<string, unknown>).defaultUtilisationTarget != null
        ? Number((settings as unknown as Record<string, unknown>).defaultUtilisationTarget)
        : DEFAULT_TARGET_PCT;

    // Pre-bucket bookings by resourceId AND resourceIds (multi-resource bookings)
    const bookingsByResource: Record<string, Booking[]> = {};
    for (const b of bookings) {
      const seenForBooking = new Set<string>();
      const rid = b.resourceId ?? '';
      if (rid && rid !== 'unallocated') {
        seenForBooking.add(rid);
        if (!bookingsByResource[rid]) bookingsByResource[rid] = [];
        bookingsByResource[rid].push(b);
      }
      if (b.resourceIds) {
        for (const id of b.resourceIds) {
          if (id && !seenForBooking.has(id)) {
            seenForBooking.add(id);
            if (!bookingsByResource[id]) bookingsByResource[id] = [];
            bookingsByResource[id].push(b);
          }
        }
      }
    }

    // Next 12 ISO weeks starting from today
    const next12WeekStarts = eachWeekOfInterval(
      {
        start: today,
        end:   addDays(today, 7 * 12),
      },
      { weekStartsOn: 1 },
    ).slice(0, 12);

    const allMetrics: ResourceForecastMetrics[] = filtered.map((r) => {
      const profile = getEffectiveProfile(r, globalProfile);
      return computeResourceForecast(
        r,
        bookingsByResource[r.id] ?? [],
        dateFrom,
        dateTo,
        profile,
        targetPct,
        next12WeekStarts,
        today,
      );
    });

    let rows: ForecastRow[];

    if (viewBy === 'individual' || viewBy === 'project-cohort') {
      rows = allMetrics.map((m) => ({
        id:             m.resource.id,
        name:           m.resource.name,
        role:           m.resource.role,
        department:     m.resource.schedulerGroup ?? m.resource.department ?? '—',
        targetPct:      m.targetPct,
        bookedPct:      m.bookedPct,
        projectedPct:   m.projectedPct,
        variancePct:    m.variancePct,
        bookedHours:    m.bookedHours,
        plannedHours:   m.plannedHours,
        availableHours: m.availableHours,
        status:         resolveStatus(m.bookedPct, m.targetPct, m.bookedHours, m.plannedHours),
        initials:       getInitials(m.resource.name),
        photoUrl:       m.resource.photoUrl,
        bookedColour:   resolveColour(m.bookedPct, m.targetPct),
        periodBookings: m.periodBookings,
        weeklyForecast: m.weeklyForecast,
        hasRiskFlag:    m.hasRiskFlag,
        riskPct:        m.riskPct,
        riskWeek:       m.riskWeek,
        memberCount:    1,
      }));
    } else {
      const groups = new Map<string, ResourceForecastMetrics[]>();
      for (const m of allMetrics) {
        const key = groupKeyFor(m.resource, viewBy);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(m);
      }
      rows = Array.from(groups.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, members]) => aggregateForecastGroup(key, members));
    }

    const chartData: ForecastChartPoint[] = rows.map((row) => ({
      name:      viewBy === 'individual' ? row.name.split(' ')[0] : row.name,
      booked:    row.bookedPct,
      projected: row.projectedPct,
      target:    row.targetPct,
    }));

    const activeRows = rows.filter((r) => r.bookedHours > 0 || r.plannedHours > 0);
    const avgBooked = activeRows.length > 0
      ? Math.round(activeRows.reduce((s, r) => s + r.bookedPct, 0) / activeRows.length)
      : 0;
    const avgProjected = activeRows.length > 0
      ? Math.round(activeRows.reduce((s, r) => s + r.projectedPct, 0) / activeRows.length)
      : 0;
    const totalBookedHours  = rows.reduce((s, r) => s + r.bookedHours, 0);
    const resourcesOnTarget = rows.filter(
      (r) => r.bookedPct >= r.targetPct && (r.bookedHours > 0 || r.plannedHours > 0),
    ).length;
    const atRiskCount = rows.filter((r) => r.hasRiskFlag).length;

    const isEmpty = rows.length === 0;

    return {
      rows,
      chartData,
      summary: {
        avgBooked,
        avgProjected,
        totalBookedHours,
        resourcesOnTarget,
        totalResources: rows.length,
        atRiskCount,
      },
      isEmpty,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currentMode triggers re-computation when switching demo/live
  }, [resources, bookings, settings, dateFrom, dateTo, viewBy, selectedResourceIds, selectedDepartments, selectedTeams, selectedRoles, currentMode]);

  return { ...data, isLoading: isLiveLoading };
}
